<template>
    <div class="bg-white border border-border-primary rounded-lg shadow-xl h-[400px] w-[640px] overflow-hidden flex flex-col">
        <!-- Unified safe zone overlay -->
        <SafeZoneOverlay
            v-if="safeZoneSourcePosition && safeZoneTargetPosition"
            :source-position="safeZoneSourcePosition"
            :target-position="safeZoneTargetPosition"
        />

        <!-- Search Input -->
        <div class="p-3 border-b border-border-primary">
            <TextInput
                v-model="searchQuery"
                placeholder="Search portfolios and assets..."
                leading-icon="magnifying-glass"
                autofocus
                class="border-none"
            />
        </div>

        <!-- Search Results View -->
        <div v-if="isSearching" class="flex-1 overflow-y-auto">
            <div v-if="searchResults.length === 0" class="p-4 text-center text-text-secondary paragraph-md">
                No results found
            </div>
            <NavigationDropdownColumn v-else title="Search Results" highlight :auto-scroll-to-current="false">
                <NavigationDropdownList placeholder="">
                    <template v-for="(result, index) in searchResults">
                        <NavigationDropdownItem
                            v-if="result.item.type === 'portfolio'"
                            :key="`${result.item.type}-${result.item.id}`"
                            :label="result.item.name ?? 'Untitled'"
                            :to="portfolioRoutePreserving(result.item)"
                            :current="result.item.id === currentPortfolioId"
                            :selected="index === focusedIndex"
                            type="Portfolio"
                            @mousemove="focusedIndex = index"
                            @click="closeDropdown"
                        />
                        <NavigationDropdownItem
                            v-if="result.item.type === 'asset'"
                            :key="`${result.item.type}-${result.item.id}`"
                            :label="result.item.name ?? 'Untitled'"
                            :parent="result.item.portfolio.name"
                            :badge="result.matchedBy === 'clientId' ? result.item.client_id! : undefined"
                            :to="assetRoutePreserving(result.item)"
                            :current="result.item.id === currentAssetId"
                            :selected="index === focusedIndex"
                            :inactive="!result.item.active"
                            type="Asset"
                            @mousemove="focusedIndex = index"
                            @click="closeDropdown"
                        />
                    </template>
                </NavigationDropdownList>
            </NavigationDropdownColumn>
        </div>

        <!-- 2-column layout: Portfolio + Asset -->
        <div v-else class="flex flex-1 overflow-hidden">
            <!-- Portfolios Column -->
            <NavigationDropdownColumn
                ref="portfoliosColumn"
                title="Portfolios"
                class="w-80"
                :highlight="triggerSource === 'portfolio'"
                :view-all-link="portfoliosViewAllLink"
                @view-all-click="closeDropdown"
            >
                <NavigationDropdownList>
                    <NavigationDropdownItem
                        v-for="portfolio in portfolios"
                        :key="portfolio.id"
                        :label="portfolio.name ?? 'Untitled'"
                        :to="portfolioRoutePreserving(portfolio)"
                        :current="portfolio.id === currentPortfolioId"
                        :selected="portfolio.id === selectedPortfolioId"
                        @mousemove="(event: MouseEvent) => selectPortfolio(portfolio.id, event)"
                        @click="closeDropdown"
                    />
                </NavigationDropdownList>
            </NavigationDropdownColumn>

            <!-- Assets Column -->
            <NavigationDropdownColumn
                ref="assetsColumn"
                title="Assets"
                class="w-80"
                :highlight="triggerSource === 'asset'"
                :view-all-link="assetsViewAllLink"
                @view-all-click="closeDropdown"
            >
                <NavigationDropdownList :placeholder="selectedPortfolioId ? 'No assets in this portfolio' : 'Hover over a portfolio to see its assets'">
                    <NavigationDropdownItem
                        v-for="asset in filteredAssets"
                        :key="asset.id"
                        :label="asset.name ?? 'Untitled'"
                        :to="assetRoutePreserving(asset)"
                        :current="asset.id === currentAssetId"
                        :selected="asset.id === selectedAssetId"
                        :inactive="!asset.active"
                        @mousemove="selectAsset(asset.id)"
                        @click="closeDropdown"
                    />
                </NavigationDropdownList>
            </NavigationDropdownColumn>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import TextInput from '@/vue/components/design-system/input/TextInput.vue'
import type { BreadcrumbAsset, BreadcrumbPortfolio } from '../breadcrumb.types'
import { useBreadcrumb_experimental } from '../use-breadcrumbs'
import { useCompanyHierarchy } from '../use-company-hierarchy'
import { useCompanyList } from '../use-company-list'
import { useKeyboardNavigation } from '../use-keyboard-navigation'
import { useNavigationRoutes } from '../use-navigation-routes'
import { useNavigationSearch } from '../use-navigation-search'
import NavigationDropdownColumn from './ui/navigation-dropdown-column.vue'
import NavigationDropdownItem from './ui/navigation-dropdown-item.vue'
import NavigationDropdownList from './ui/navigation-dropdown-list.vue'
import SafeZoneOverlay from './ui/safe-zone-overlay.vue'

// Custom components for hierarchy

type TriggerSource = 'portfolio' | 'asset'

type DropdownPosition = {
    x: number
    y: number
    width: number
    height: number
}

type Props = {
    isOpen: boolean
    triggerSource?: TriggerSource | null
}

const props = withDefaults(defineProps<Props>(), {
    triggerSource: null,
})

const emit = defineEmits<{
    'update:isOpen': [value: boolean]
}>()

const router = useRouter()
const { getCompanyById } = useCompanyList()
const { portfolios: companyPortfolios, assets: companyAssets } = useCompanyHierarchy()
const { company, portfolio, asset, getPortfolioById } = useBreadcrumb_experimental()
const { portfolioRoutePreserving, assetRoutePreserving, portfolioListRoute, companyRoute } = useNavigationRoutes()

// Template refs
const portfoliosColumn = useTemplateRef('portfoliosColumn')
const assetsColumn = useTemplateRef('assetsColumn')

// Column positions for safe zone calculation (used by hover safe zones)
const portfoliosColumnPosition = ref<DropdownPosition>({ x: 0, y: 0, width: 0, height: 0 })
const assetsColumnPosition = ref<DropdownPosition>({ x: 0, y: 0, width: 0, height: 0 })

// Safe zone state for hover-based safe zones between columns
const safeZoneSourcePosition = ref<DropdownPosition | null>(null)
const safeZoneTargetPosition = ref<DropdownPosition | null>(null)

// Update column positions when mounted and when dropdown opens
onMounted(async () => {
    await nextTick()
    setTimeout(() => {
        updatePortfoliosColumnPosition()
        updateAssetsColumnPosition()
    }, 100)
})

watch([() => props.isOpen, () => props.triggerSource], async () => {
    if (props.isOpen) {
        await nextTick()
        updatePortfoliosColumnPosition()
        updateAssetsColumnPosition()
    } else {
        // Clear safe zone when dropdown closes
        clearSafeZone()
    }
})

function updatePortfoliosColumnPosition() {
    if (portfoliosColumn.value) {
        const rect = portfoliosColumn.value.$el.getBoundingClientRect()
        portfoliosColumnPosition.value = {
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
        }
    }
}

function updateAssetsColumnPosition() {
    if (assetsColumn.value) {
        const rect = assetsColumn.value.$el.getBoundingClientRect()
        assetsColumnPosition.value = {
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
        }
    }
}

const currentCompanyId = computed(() => company.value?.id || null)
const currentPortfolioId = computed(() => portfolio.value?.id || null)
const currentAssetId = computed(() => asset.value?.id || null)

// View All links configuration
const portfoliosViewAllLink = computed(() => {
    if (!company.value) {
        return undefined
    }
    return {
        to: companyRoute(company.value),
        label: 'View All',
    }
})

const assetsViewAllLink = computed(() => {
    if (!selectedPortfolioId.value) {
        return undefined
    }
    const selectedPortfolio = getPortfolioById(selectedPortfolioId.value)!

    // In edit mode, disable for now (will be added later per requirements)
    return {
        to: portfolioListRoute(selectedPortfolio),
        label: 'View All',
    }
})

// Hover-based selection state (for showing related items)
const selectedPortfolioId = ref<number | null>(null)
const selectedAssetId = ref<number | null>(null)

// Initialize with current route to show relevant data by default
onMounted(() => {
    if (props.triggerSource === 'asset') {
        selectedPortfolioId.value = currentPortfolioId.value
        selectedAssetId.value = currentAssetId.value
    }
})

// Computed data - get portfolios from current company
const portfolios = computed(() => {
    if (!currentCompanyId.value) {
        return []
    }
    const company = getCompanyById(currentCompanyId.value)
    return company?.portfolios || []
})

const filteredAssets = computed(() => {
    if (!selectedPortfolioId.value) {
        return []
    }
    const portfolio = portfolios.value.find(p => p.id === selectedPortfolioId.value)
    return portfolio?.assets || []
})

// Search functionality
const searchQuery = ref('')

// Combine portfolios and assets for search
const allSearchableItems = computed<(BreadcrumbPortfolio | BreadcrumbAsset)[]>(() => {
    return [...companyPortfolios.value, ...companyAssets.value]
})

// Context-aware search: prioritize results based on where dropdown was opened from
const priorityType = computed(() => props.triggerSource)
const { isSearching, searchResults } = useNavigationSearch(allSearchableItems, searchQuery, {
    priorityType,
    currentPortfolioId,
})

// Keyboard navigation (only active when searching)
const { focusedIndex } = useKeyboardNavigation({
    items: searchResults,
    searchQuery,
    onSelect: (result) => {
        closeDropdown()
        if (result.item.type === 'portfolio') {
            router.push(portfolioRoutePreserving(result.item))
        } else {
            router.push(assetRoutePreserving(result.item))
        }
    },
})

// Helper function to create safe zone from mouse position
function createHoverSafeZone(event: MouseEvent, targetColumn: DropdownPosition) {
    // Create source position from mouse coordinates
    safeZoneSourcePosition.value = {
        x: event.clientX + 1,
        y: event.clientY,
        width: 0, // Point source
        height: 0,
    }
    safeZoneTargetPosition.value = targetColumn
}

// Clear safe zone when dropdown closes or mouse leaves
function clearSafeZone() {
    safeZoneSourcePosition.value = null
    safeZoneTargetPosition.value = null
}

// Selection handlers (for hover)
function selectPortfolio(portfolioId: number, event?: MouseEvent) {
    selectedPortfolioId.value = portfolioId
    selectedAssetId.value = null

    // Create safe zone from portfolio hover to assets column
    if (event) {
        createHoverSafeZone(event, assetsColumnPosition.value)
    }
}

function selectAsset(assetId: number) {
    selectedAssetId.value = assetId
    // No safe zone needed for assets (last column)
}

// Close dropdown handler
function closeDropdown() {
    emit('update:isOpen', false)
}

</script>
